import { useState } from "react";
import { useGameStore } from "../../store/gameStore";
import { useUIStore } from "../../store/uiStore";
import { useApi } from "../../hooks/useApi";
import { hapticFeedback, hapticNotification } from "../../telegram/init";
import { Timer } from "../shared/Timer";
import { RESEARCH_DEFINITIONS, ResearchType, getResearchCost, getResearchTime } from "@ikarium/shared";

const RESEARCH_ICONS: Record<string, string> = {
  CONSERVATION:     "\u267B\uFE0F",
  PULLEY:           "\u2699\uFE0F",
  CARTOGRAPHY:      "\u{1F5FA}\uFE0F",
  TAXATION:         "\u{1F4B0}",
  IRRIGATION:       "\u{1F4A7}",
  BRONZE_ARMOR:     "\u{1F6E1}\uFE0F",
  SHARPENED_BLADES: "\u2694\uFE0F",
  FORMATIONS:       "\u{1F3DB}\uFE0F",
  SIEGE_CRAFT:      "\u{1F4A3}",
  NAVAL_WARFARE:    "\u2693",
};

function formatTime(seconds: number): string {
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

export function ResearchPanel() {
  const closePanel          = useUIStore((s) => s.closePanel);
  const researchType        = useUIStore((s) => s.selectedResearchType);
  const researches          = useGameStore((s) => s.researches);
  const cities              = useGameStore((s) => s.cities);
  const activeCityId        = useGameStore((s) => s.activeCityId);
  const updateCity          = useGameStore((s) => s.updateCity);
  const setResearches       = useGameStore((s) => s.setResearches);
  const api                 = useApi();

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  if (!researchType) return null;

  const type = researchType as ResearchType;
  const def  = RESEARCH_DEFINITIONS[type];
  if (!def) return null;

  const city     = cities.find((c) => c.id === activeCityId);
  const research = researches.find((r) => r.researchType === researchType) ?? {
    researchType,
    level: 0,
    isResearching: false,
    researchEndAt: null,
  };

  const currentLevel = research.level;
  const nextLevel    = currentLevel + 1;
  const isMaxed      = currentLevel >= def.maxLevel;
  const isResearching = research.isResearching;

  // Prerequisite check
  let prereqMet = true;
  let prereqLabel = "";
  if (def.prerequisite) {
    const prereqResearch = researches.find((r) => r.researchType === def.prerequisite!.type);
    const prereqLevel = prereqResearch?.level ?? 0;
    prereqMet = prereqLevel >= def.prerequisite.level;
    const prereqDef = RESEARCH_DEFINITIONS[def.prerequisite.type];
    prereqLabel = `${prereqDef?.name ?? def.prerequisite.type} Lv.${def.prerequisite.level}`;
  }

  // Academy check
  const hasAcademy = city?.buildings.some((b) => b.buildingType === "ACADEMY" && b.level >= 1) ?? false;

  // Cost
  const cost     = !isMaxed ? getResearchCost(type, nextLevel) : { wood: 0, marble: 0, crystal: 0, sulfur: 0 };
  const time     = !isMaxed ? getResearchTime(type, nextLevel) : 0;

  // Other research in progress?
  const otherResearching = researches.some((r) => r.isResearching && r.researchType !== researchType);

  const canStart = !isMaxed && !isResearching && !otherResearching && prereqMet && hasAcademy
    && (city ? city.wood >= cost.wood && city.marble >= cost.marble && city.crystal >= cost.crystal && city.sulfur >= cost.sulfur : false);

  function buttonLabel() {
    if (loading)          return "Starting…";
    if (isMaxed)          return "Max Level Reached";
    if (isResearching)    return "In Progress…";
    if (otherResearching) return "Another Research Active";
    if (!hasAcademy)      return "Academy Required";
    if (!prereqMet)       return `Requires ${prereqLabel}`;
    return "Start Research";
  }

  async function handleStart() {
    if (!activeCityId || !canStart || !researchType) return;
    setLoading(true);
    setError(null);
    try {
      await api.startResearch(researchType, activeCityId);
      hapticNotification("success");
      const [cityData, resData] = await Promise.all([
        api.getCity(activeCityId),
        api.getResearches(),
      ]);
      updateCity({ id: activeCityId, ...cityData });
      setResearches(resData.researches);
      closePanel();
    } catch (e: any) {
      setError(e.message);
      hapticNotification("error");
    } finally {
      setLoading(false);
    }
  }

  const icon = RESEARCH_ICONS[researchType] ?? "\u{1F4DC}";

  return (
    <>
      <div className="panel-overlay" onClick={closePanel} />
      <div className="panel-sheet">
        <div className="panel-handle" />

        {/* Header */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-sm)", marginBottom: "var(--space-lg)" }}>
          <span style={{ fontSize: "2.5rem" }}>{icon}</span>
          <h2 style={{ color: "var(--terra-dark)", margin: 0 }}>{def.name}</h2>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
            <span style={{
              background: isMaxed ? "var(--olive)" : "var(--terra)",
              color: "var(--papyrus-light)",
              fontFamily: "var(--font-display)",
              fontSize: "0.7rem",
              fontWeight: 700,
              padding: "2px 10px",
              borderRadius: "10px",
            }}>
              {isMaxed ? "MAX" : `Level ${currentLevel}`}
            </span>
            {isResearching && research.researchEndAt && (
              <Timer endAt={research.researchEndAt} />
            )}
          </div>
        </div>

        {/* Description */}
        <p style={{
          textAlign: "center",
          color: "var(--ink-faded)",
          marginBottom: "var(--space-md)",
          fontSize: "0.9rem",
          fontStyle: "italic",
        }}>
          {def.description}
        </p>

        {/* Effect */}
        <div style={{
          padding: "var(--space-md)",
          background: "linear-gradient(135deg, rgba(91,117,83,0.08), rgba(122,154,111,0.04))",
          borderRadius: "var(--radius)",
          border: "1px solid rgba(91,117,83,0.2)",
          marginBottom: "var(--space-lg)",
          fontSize: "0.82rem",
          color: "var(--olive)",
          textAlign: "center",
        }}>
          {def.effect}
          {currentLevel > 0 && !isMaxed && (
            <div style={{ color: "var(--ink-faded)", marginTop: "2px", fontSize: "0.75rem" }}>
              Current: Lv {currentLevel} → Next: Lv {nextLevel}
            </div>
          )}
        </div>

        {/* Prerequisite status */}
        {def.prerequisite && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-sm)",
            padding: "var(--space-sm) var(--space-md)",
            borderRadius: "var(--radius)",
            background: prereqMet ? "rgba(91,117,83,0.06)" : "rgba(196,91,58,0.06)",
            border: `1px solid ${prereqMet ? "rgba(91,117,83,0.2)" : "rgba(196,91,58,0.2)"}`,
            marginBottom: "var(--space-lg)",
            fontSize: "0.8rem",
          }}>
            <span>{prereqMet ? "\u2705" : "\u274C"}</span>
            <span style={{ color: prereqMet ? "var(--olive)" : "var(--terra-dark)" }}>
              Requires {prereqLabel}
            </span>
          </div>
        )}

        {/* Academy check */}
        {!hasAcademy && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-sm)",
            padding: "var(--space-sm) var(--space-md)",
            borderRadius: "var(--radius)",
            background: "rgba(196,91,58,0.06)",
            border: "1px solid rgba(196,91,58,0.2)",
            marginBottom: "var(--space-lg)",
            fontSize: "0.8rem",
            color: "var(--terra-dark)",
          }}>
            <span>{"\u{1F4DA}"}</span>
            <span>Build an Academy (Lv 1+) to conduct research</span>
          </div>
        )}

        {/* Cost + time (if not maxed and not already in progress) */}
        {!isMaxed && !isResearching && (
          <div style={{
            background: "var(--parchment)",
            borderRadius: "var(--radius)",
            padding: "var(--space-md)",
            marginBottom: "var(--space-lg)",
          }}>
            <div style={{
              fontFamily: "var(--font-display)",
              fontSize: "0.65rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--ink-faded)",
              marginBottom: "var(--space-sm)",
            }}>
              Cost to Research Level {nextLevel}
            </div>
            <CostRow icon="\u{1FAB5}" label="Wood"    amount={cost.wood}    has={city?.wood ?? 0} />
            {cost.marble  > 0 && <CostRow icon="\u2B1C" label="Marble"  amount={cost.marble}  has={city?.marble ?? 0} />}
            {cost.crystal > 0 && <CostRow icon="\u{1F48E}" label="Crystal" amount={cost.crystal} has={city?.crystal ?? 0} />}
            {cost.sulfur  > 0 && <CostRow icon="\u{1F525}" label="Sulfur"  amount={cost.sulfur}  has={city?.sulfur ?? 0} />}
            <div style={{
              marginTop: "var(--space-sm)",
              fontSize: "0.8rem",
              color: "var(--ink-faded)",
            }}>
              {"\u23F1\uFE0F"} {formatTime(time)}
            </div>
          </div>
        )}

        {/* In progress card */}
        {isResearching && research.researchEndAt && (
          <div style={{
            textAlign: "center",
            padding: "var(--space-lg)",
            background: "linear-gradient(135deg, rgba(201,169,97,0.1), rgba(218,183,82,0.05))",
            borderRadius: "var(--radius)",
            border: "1px solid rgba(201,169,97,0.3)",
            marginBottom: "var(--space-lg)",
          }}>
            <div style={{
              fontFamily: "var(--font-display)",
              fontSize: "0.75rem",
              color: "var(--gold-dim)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "var(--space-sm)",
            }}>
              Researching…
            </div>
            <Timer endAt={research.researchEndAt} />
          </div>
        )}

        {/* Start button */}
        {!isMaxed && !isResearching && (
          <button
            className="btn-primary"
            disabled={!canStart || loading}
            onClick={handleStart}
            style={{ width: "100%", padding: "14px" }}
          >
            {buttonLabel()}
          </button>
        )}

        {/* Error */}
        {error && (
          <div style={{
            marginTop: "var(--space-md)",
            padding: "var(--space-md)",
            background: "rgba(196, 91, 58, 0.1)",
            border: "1px solid rgba(196, 91, 58, 0.3)",
            borderRadius: "var(--radius)",
            color: "var(--terra-dark)",
            fontSize: "0.85rem",
            textAlign: "center",
          }}>
            {error}
          </div>
        )}
      </div>
    </>
  );
}

function CostRow({ icon, label, amount, has }: { icon: string; label: string; amount: number; has: number }) {
  const canAfford = has >= amount;
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "3px 0",
      fontSize: "0.85rem",
    }}>
      <span style={{ color: "var(--ink-faded)" }}>{icon} {label}</span>
      <span style={{
        fontWeight: 700,
        color: canAfford ? "var(--olive)" : "var(--terra)",
      }}>
        {amount.toLocaleString()}
        <span style={{ fontWeight: 400, color: "var(--ink-faded)", fontSize: "0.75rem" }}>
          {" "}/ {Math.floor(has).toLocaleString()}
        </span>
      </span>
    </div>
  );
}
